import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Router } from '@angular/router';

export interface LogoutEvent {
  initiator: 'menu' | 'app' | 'system';
  timestamp: number;
  reason?: string;
}

export interface LogoutStatus {
  isLoggingOut: boolean;
  phase: 'idle' | 'device-cleanup' | 'agent-state' | 'websocket-disconnect' | 'server-logout' | 'storage-cleanup' | 'redirect';
  initiator?: string;
  startTime?: number;
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class LogoutCoordinatorService {
  private logoutEvent$ = new Subject<LogoutEvent>();
  private logoutStatus$ = new Subject<LogoutStatus>();
  private currentStatus: LogoutStatus = { isLoggingOut: false, phase: 'idle' };



  constructor(private router: Router) {}

  /**
   * Subscribe to logout events
   */
  public onLogoutEvent(): Observable<LogoutEvent> {
    return this.logoutEvent$.asObservable();
  }

  /**
   * Subscribe to logout status updates
   */
  public onLogoutStatus(): Observable<LogoutStatus> {
    return this.logoutStatus$.asObservable();
  }

  /**
   * Get current logout status
   */
  public getCurrentStatus(): LogoutStatus {
    return { ...this.currentStatus };
  }


  /**
   * Initiate logout process
   */
  public async initiateLogout(initiator: 'menu' | 'app' | 'system', reason?: string): Promise<void> {
    if (this.currentStatus.isLoggingOut) {
      // console.log`⚠️ Logout ya en progreso, iniciado por: ${this.currentStatus.initiator}`);
      return;
    }

    // console.log`🔐 Iniciando logout coordinado desde: ${initiator}`);

    const logoutEvent: LogoutEvent = {
      initiator,
      timestamp: Date.now(),
      reason
    };

    this.emitLogoutEvent(logoutEvent);
    this.updateStatus({ isLoggingOut: true, phase: 'device-cleanup', initiator, startTime: Date.now() });

    try {
      // The actual logout logic will be handled by the component that initiated the logout
      // This service coordinates the WebSocket disconnection part

    } catch (error) {
      console.error('❌ Error durante logout coordinado:', error);
      this.updateStatus({ ...this.currentStatus, error });
      throw error;
    }
  }

  /**
   * Update logout phase
   */
  public updateLogoutPhase(phase: LogoutStatus['phase']): void {
    this.updateStatus({ ...this.currentStatus, phase });
  }


  /**
   * Complete logout process
   */
  public completeLogout(): void {
    this.updateStatus({ ...this.currentStatus, phase: 'redirect' });

    setTimeout(() => {
      this.updateStatus({ isLoggingOut: false, phase: 'idle' });
      // console.log'✅ Proceso de logout coordinado completado');
    }, 1000);
  }

  /**
   * Handle logout error
   */
  public handleLogoutError(error: any): void {
    console.error('❌ Error en logout coordinado:', error);
    this.updateStatus({ ...this.currentStatus, error });

    // Reset status after error
    setTimeout(() => {
      this.updateStatus({ isLoggingOut: false, phase: 'idle' });
    }, 2000);
  }

  /**
   * Emergency logout - force logout even if coordination fails
   */
  public async emergencyLogout(): Promise<void> {
    console.warn('🚨 Logout de emergencia activado');



    // Force cleanup
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigate(['/login']);
    this.completeLogout();
  }

  /**
   * Check if logout is in progress
   */
  public isLoggingOut(): boolean {
    return this.currentStatus.isLoggingOut;
  }



  // Private methods
  private emitLogoutEvent(event: LogoutEvent): void {
    this.logoutEvent$.next(event);
  }

  private updateStatus(status: LogoutStatus): void {
    this.currentStatus = status;
    this.logoutStatus$.next(status);
  }
}
